import { useTranslation } from 'react-i18next';
import { Ref, useEffect } from 'react';

import { Button, Card, CardContent, Heading, NumberInput } from '@app/shared/ui/atoms/Common';
import { TonConnectButton } from '@tonconnect/ui-react';
import { PriceType } from '@app/shared/types/common';
import { cookieImage, diamondIcon, questionIcon } from '@app/assets';
import { useLazyGetTonPriceQuery } from '@app/store/slices/tonPrice/tonPriceApi';
import { EXCHANGE_RATES, MIN_DEPOSIT_COOKIE, MIN_DEPOSIT_TON } from '@app/shared/constants/common';
import { cn, defaultTelegramVibrate } from '@app/shared/utils/common';
import { Link } from 'react-router-dom';

interface DepositSectionProps {
  isTopUpDisabled?: boolean;
  amount: string;
  onAmountChange: (value: string) => void;
  onTopUpBtnClick: (currency?: PriceType) => void;
  scrollRef: Ref<HTMLDivElement> | null;
  currency?: PriceType;
  isSuccessful?: boolean;
  isLocked?: boolean;
  isWallet?: boolean;
}

export function DepositSection({
  isTopUpDisabled = true,
  amount,
  onAmountChange,
  onTopUpBtnClick,
  scrollRef,
  currency = PriceType.TON,
  isSuccessful = false,
  isLocked = false,
  isWallet = false,
}: DepositSectionProps) {
  const { t } = useTranslation();
  const [getTonPrice, { data: tonPrice }] = useLazyGetTonPriceQuery();

  useEffect(() => {
    void getTonPrice();
  }, []);

  const numericAmount = Number(amount);

  const isBelowMinimum =
    currency === PriceType.TON
      ? numericAmount < MIN_DEPOSIT_TON
      : numericAmount < MIN_DEPOSIT_COOKIE;

  return (
    <Card ref={scrollRef} className="mx-auto w-full rounded-xl bg-blue-light pt-6">
      <CardContent className="space-y-3">
        <div className={'relative'}>
          <Heading
            level={5}
            className="text-center text-accent flex items-center justify-center gap-1"
          >
            {t('settlement_market.balance')}
            <img
              src={currency === PriceType.TON ? diamondIcon : cookieImage}
              className="size-4"
              alt="currency icon"
            />
          </Heading>
          <Button
            variant="custom"
            className="flex -right-3 -top-3 absolute size-8 items-center justify-center rounded-full bg-[#3E3B3B99]"
            onClick={defaultTelegramVibrate}
            asChild
          >
            <Link to={'https://teletype.in/@dom_pandora/instruction'} target={'_blank'}>
              <img src={questionIcon} alt="payment tutorial" className="size-5 text-foreground" />
            </Link>
          </Button>
        </div>
        <div className="relative">
          <NumberInput
            value={amount}
            min={1}
            step={1}
            onChange={(e) => {
              onAmountChange(e.target.value);
            }}
            disabled={isLocked}
            placeholder={t('settlement_market.enter_amount')}
            className="rounded-xl border-2 border-accent bg-white focus:border-[#4ec3ff] disabled:bg-[#ccc] disabled:bg-none disabled:text-slate-700"
          />
          {tonPrice && (
            <span className="absolute text-secondary-foreground top-1/2 -translate-y-1/2 right-2">
              {currency === PriceType.TON
                ? `~${(tonPrice * Number(amount)).toFixed(2)} $`
                : `~${((tonPrice * Number(amount)) / EXCHANGE_RATES[PriceType.COOKIE]).toFixed(2)} $`}
            </span>
          )}
        </div>

        <div className="space-y-2">
          <TonConnectButton className={cn('ton-button', isSuccessful && 'success')} />
          {isWallet && !isLocked && (
            <Button
              variant="custom"
              className="cursor-pointer w-full bg-linear-180 from-[#093c83] to-[#058ed2] px-5 text-foreground disabled:bg-[#ccc] disabled:bg-none"
              disabled={isBelowMinimum}
              onClick={() => {
                onTopUpBtnClick(currency);
              }}
            >
              {!isSuccessful && t('settlement_market.top_up')}
              {isSuccessful && t('settlement_market.top_up_successful')} TON 💎
            </Button>
          )}
          {isLocked && (
            <Button
              variant="custom"
              className="w-full bg-linear-180 from-[#093c83] to-[#058ed2] px-5 text-lg text-white text-center loading"
            >
              {t('settlement_market.top_up_check')}
            </Button>
          )}
          {isBelowMinimum && amount && !isTopUpDisabled && (
            <p className="text-xs text-red-500 text-center">
              {currency === PriceType.TON
                ? t('errors.min_ton', { value: MIN_DEPOSIT_TON })
                : t('errors.min_cookies', { value: MIN_DEPOSIT_COOKIE })}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
